import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Zap,
  Users,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  RefreshCw,
  Target,
  Award,
  PieChart,
  ChevronDown,
  Info,
  Loader2,
  X,
  Coins,
  Calendar,
  CalendarDays,
  CalendarRange,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { AI_EMPLOYEES } from '../data/employees';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface OverviewData {
  tasks_completed: number;
  messages_sent: number;
  time_saved_hours: number;
  cost_savings: number;
  credits_used: number;
  // Comparison data for trend indicators
  prev_tasks_completed?: number;
  prev_messages_sent?: number;
  prev_time_saved_hours?: number;
  prev_cost_savings?: number;
}

interface CreditUsageData {
  daily: number;
  weekly: number;
  monthly: number;
  total: number;
  daily_breakdown: Array<{ date: string; credits: number }>;
}

interface DailyUsage {
  day: string;
  date: string;
  tasks: number;
  messages: number;
}

interface EmployeePerformance {
  employee_id: string;
  tasks: number;
  completed: number;
  failed: number;
  efficiency: number;
}

interface CategoryUsage {
  category: string;
  value: number;
  percentage: number;
}

// Get employee info
const getEmployeeInfo = (employeeId: string) => {
  const employee = AI_EMPLOYEES.find(e => e.id === employeeId);
  return employee || { name: employeeId, role: 'AI Employee', avatar: '🤖', gradient: 'from-gray-500 to-gray-600' };
};

// Format date for Excel compatibility (MM/DD/YYYY)
const formatDateForExcel = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Generate CSV content from analytics data
const generateCSV = (
  overview: OverviewData | null,
  dailyUsage: DailyUsage[],
  employeePerformance: EmployeePerformance[],
  categoryUsage: { categories: CategoryUsage[]; total: number },
  timeRange: string,
  creditUsage: CreditUsageData | null
): string => {
  const lines: string[] = [];
  const dateGenerated = new Date().toLocaleString();
  const rangeLabel = timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : timeRange === '90d' ? 'Last 90 days' : 'Last year';

  // Header
  lines.push('SweetPilot Analytics Report');
  lines.push(`Generated: ${dateGenerated}`);
  lines.push(`Time Range: ${rangeLabel}`);
  lines.push('');

  // Overview Section
  lines.push('=== OVERVIEW ===');
  lines.push('Metric,Value');
  lines.push(`Tasks Completed,${overview?.tasks_completed || 0}`);
  lines.push(`Messages Sent,${overview?.messages_sent || 0}`);
  lines.push(`Time Saved (hours),${overview?.time_saved_hours || 0}`);
  lines.push(`Cost Savings ($),${overview?.cost_savings || 0}`);
  lines.push(`Credits Used,${overview?.credits_used || 0}`);
  lines.push('');

  // Daily Usage Section
  lines.push('=== DAILY USAGE (Last 7 Days) ===');
  lines.push('Date,Day,Tasks,Messages');
  dailyUsage.forEach(day => {
    const formattedDate = formatDateForExcel(day.date);
    lines.push(`${formattedDate},${day.day},${day.tasks},${day.messages}`);
  });
  lines.push('');

  // Employee Performance Section
  lines.push('=== EMPLOYEE PERFORMANCE ===');
  lines.push('Employee,Role,Tasks Completed,Failed,Efficiency (%)');
  employeePerformance.forEach(emp => {
    const info = getEmployeeInfo(emp.employee_id);
    lines.push(`${info.name},${info.role},${emp.completed},${emp.failed},${emp.efficiency}`);
  });
  lines.push('');

  // Category Usage Section
  lines.push('=== USAGE BY CATEGORY ===');
  lines.push('Category,Tasks,Percentage (%)');
  categoryUsage.categories.forEach(cat => {
    lines.push(`${cat.category},${cat.value},${cat.percentage}`);
  });
  lines.push(`Total,${categoryUsage.total},100`);
  lines.push('');

  // Credit Usage Section
  lines.push('=== CREDIT USAGE ===');
  lines.push('Period,Credits Used');
  lines.push(`Today,${creditUsage?.daily || 0}`);
  lines.push(`This Week,${creditUsage?.weekly || 0}`);
  lines.push(`This Month,${creditUsage?.monthly || 0}`);
  lines.push(`All Time,${creditUsage?.total || 0}`);
  lines.push('');

  // Daily Credit Breakdown
  if (creditUsage?.daily_breakdown && creditUsage.daily_breakdown.length > 0) {
    lines.push('=== DAILY CREDIT BREAKDOWN (Last 7 Days) ===');
    lines.push('Date,Credits Used');
    creditUsage.daily_breakdown.forEach(day => {
      const formattedDate = formatDateForExcel(day.date);
      lines.push(`${formattedDate},${day.credits}`);
    });
  }

  return lines.join('\n');
};

// Download CSV file
const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Color palette for categories
const categoryColors = [
  'bg-pink-500',
  'bg-blue-500',
  'bg-purple-500',
  'bg-green-500',
  'bg-amber-500',
  'bg-cyan-500',
  'bg-red-500',
  'bg-indigo-500',
];

const categoryHexColors = [
  '#ec4899',
  '#3b82f6',
  '#a855f7',
  '#22c55e',
  '#f59e0b',
  '#06b6d4',
  '#ef4444',
  '#6366f1',
];

export default function Analytics() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data states
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [employeePerformance, setEmployeePerformance] = useState<EmployeePerformance[]>([]);
  const [categoryUsage, setCategoryUsage] = useState<{ categories: CategoryUsage[]; total: number }>({ categories: [], total: 0 });
  const [creditUsage, setCreditUsage] = useState<CreditUsageData | null>(null);
  const [showROIInfo, setShowROIInfo] = useState(false);

  const fetchAnalytics = async (showRefresh = false) => {
    if (!token) return;
    if (showRefresh) setRefreshing(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, dailyRes, employeeRes, categoryRes, creditRes] = await Promise.all([
        fetch(`${API_URL}/analytics/overview?range=${timeRange}`, { headers }),
        fetch(`${API_URL}/analytics/daily-usage`, { headers }),
        fetch(`${API_URL}/analytics/employee-performance`, { headers }),
        fetch(`${API_URL}/analytics/usage-by-category`, { headers }),
        fetch(`${API_URL}/analytics/credit-usage`, { headers }),
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json() as { success: boolean; data: OverviewData };
        if (data.success) setOverview(data.data);
      }

      if (dailyRes.ok) {
        const data = await dailyRes.json() as { success: boolean; data: DailyUsage[] };
        if (data.success) setDailyUsage(data.data);
      }

      if (employeeRes.ok) {
        const data = await employeeRes.json() as { success: boolean; data: EmployeePerformance[] };
        if (data.success) setEmployeePerformance(data.data);
      }

      if (categoryRes.ok) {
        const data = await categoryRes.json() as { success: boolean; data: { categories: CategoryUsage[]; total: number } };
        if (data.success) setCategoryUsage(data.data);
      }

      if (creditRes.ok) {
        const data = await creditRes.json() as { success: boolean; data: CreditUsageData };
        if (data.success) setCreditUsage(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [token, timeRange]);

  const maxTasks = Math.max(...dailyUsage.map((d) => d.tasks), 1);
  const maxMessages = Math.max(...dailyUsage.map((d) => d.messages), 1);

  // Calculate ROI metrics
  const monthlyInvestment = 299; // Base subscription cost
  const roi = overview?.cost_savings ? Math.round((overview.cost_savings / monthlyInvestment) * 100) : 0;
  const projectedAnnualSavings = (overview?.cost_savings || 0) * 12;

  // Handle export
  const handleExport = () => {
    if (loading) return;

    const csvContent = generateCSV(overview, dailyUsage, employeePerformance, categoryUsage, timeRange, creditUsage);
    const date = new Date().toISOString().split('T')[0];
    const filename = `sweetpilot-analytics-${date}.csv`;
    downloadCSV(csvContent, filename);
  };

  // Calculate trend percentages
  const calculateTrend = (current: number, previous: number): { change: string; trend: 'up' | 'down' } => {
    if (previous === 0) {
      return current > 0 ? { change: '+100%', trend: 'up' } : { change: '0%', trend: 'up' };
    }
    const diff = ((current - previous) / previous) * 100;
    const rounded = Math.round(Math.abs(diff));
    return {
      change: `${diff >= 0 ? '+' : '-'}${rounded}%`,
      trend: diff >= 0 ? 'up' : 'down',
    };
  };

  const tasksTrend = calculateTrend(
    overview?.tasks_completed || 0,
    overview?.prev_tasks_completed || 0
  );
  const messagesTrend = calculateTrend(
    overview?.messages_sent || 0,
    overview?.prev_messages_sent || 0
  );
  const timeTrend = calculateTrend(
    overview?.time_saved_hours || 0,
    overview?.prev_time_saved_hours || 0
  );
  const costTrend = calculateTrend(
    overview?.cost_savings || 0,
    overview?.prev_cost_savings || 0
  );

  // Stats cards data
  const overviewStats = [
    {
      label: 'Total Tasks Completed',
      value: overview?.tasks_completed?.toLocaleString() || '0',
      change: tasksTrend.change,
      trend: tasksTrend.trend,
      icon: Zap,
      color: 'from-purple-500 to-pink-500',
    },
    {
      label: 'AI Messages Sent',
      value: overview?.messages_sent?.toLocaleString() || '0',
      change: messagesTrend.change,
      trend: messagesTrend.trend,
      icon: MessageSquare,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      label: 'Time Saved',
      value: `${overview?.time_saved_hours || 0} hrs`,
      change: timeTrend.change,
      trend: timeTrend.trend,
      icon: Clock,
      color: 'from-green-500 to-emerald-500',
    },
    {
      label: 'Cost Savings',
      value: `$${(overview?.cost_savings || 0).toLocaleString()}`,
      change: costTrend.change,
      trend: costTrend.trend,
      icon: DollarSign,
      color: 'from-yellow-500 to-orange-500',
    },
  ];

  // Filter employees for dropdown
  const filteredPerformance = selectedEmployee === 'all'
    ? employeePerformance
    : employeePerformance.filter(e => e.employee_id === selectedEmployee);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Analytics Dashboard</h1>
          <p className={`mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Track performance, usage, and ROI metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className={`appearance-none pl-4 pr-10 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-accent-purple cursor-pointer ${isDark ? 'bg-dark-800 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          </div>
          <button
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
            className={`p-2.5 border rounded-xl transition-colors disabled:opacity-50 ${isDark ? 'bg-dark-800 border-border text-text-muted hover:text-white hover:bg-dark-700' : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-5 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              {loading ? (
                <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              ) : (
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat.trend === 'up' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {stat.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {stat.change}
                </div>
              )}
            </div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stat.value}
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Chart */}
        <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Daily Usage</h3>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Tasks completed & messages sent</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                Tasks
              </span>
              <span className={`flex items-center gap-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                Messages
              </span>
            </div>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : dailyUsage.length === 0 ? (
            <div className={`h-64 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No usage data yet</p>
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-end gap-2">
              {dailyUsage.map((day, index) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex gap-1 h-48 items-end">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${(day.tasks / maxTasks) * 100}%` }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-purple-500 to-purple-400 rounded-t-lg min-h-[4px]"
                    />
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${(day.messages / maxMessages) * 100}%` }}
                      transition={{ delay: index * 0.1 + 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t-lg opacity-60 min-h-[4px]"
                    />
                  </div>
                  <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{day.day}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage by Category */}
        <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Usage by Category</h3>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Distribution of AI tasks</p>
            </div>
            <PieChart className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          </div>

          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : categoryUsage.categories.length === 0 ? (
            <div className={`h-48 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              <div className="text-center">
                <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No category data yet</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
              {/* Donut Chart Visualization */}
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  {categoryUsage.categories.reduce((acc, category, index) => {
                    const startAngle = acc.offset;
                    const angle = (category.percentage / 100) * 360;
                    const endAngle = startAngle + angle;

                    const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                    const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                    const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                    const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);

                    const largeArc = angle > 180 ? 1 : 0;

                    acc.elements.push(
                      <motion.path
                        key={category.category}
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: index * 0.2, duration: 0.5 }}
                        d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={categoryHexColors[index % categoryHexColors.length]}
                        opacity={0.9}
                      />
                    );

                    return { elements: acc.elements, offset: endAngle };
                  }, { elements: [] as JSX.Element[], offset: 0 }).elements}
                  <circle cx="50" cy="50" r="25" className={isDark ? 'fill-dark-800' : 'fill-white'} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{categoryUsage.total.toLocaleString()}</p>
                    <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Total Tasks</p>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex-1 w-full space-y-2 sm:space-y-3">
                {categoryUsage.categories.slice(0, 5).map((category, index) => (
                  <div key={category.category} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${categoryColors[index % categoryColors.length]}`} />
                      <span className={`text-xs sm:text-sm truncate ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{category.category}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.percentage}%</span>
                      <span className={`text-xs hidden xs:inline ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{category.value.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Employee Performance */}
      <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Employee Performance</h3>
            <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Compare efficiency and output across your AI team</p>
          </div>
          <div className="relative">
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className={`appearance-none pl-4 pr-10 py-2 border rounded-xl text-sm focus:outline-none focus:border-accent-purple cursor-pointer ${isDark ? 'bg-dark-700 border-border text-white' : 'bg-white border-slate-200 text-slate-900'}`}
            >
              <option value="all">All Employees</option>
              {employeePerformance.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {getEmployeeInfo(emp.employee_id).name}
                </option>
              ))}
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          </div>
        </div>

        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : filteredPerformance.length === 0 ? (
          <div className={`h-48 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No employee performance data yet</p>
              <p className="text-sm mt-1">Start using AI employees to see their performance</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <th className={`text-left py-4 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Employee</th>
                  <th className={`text-left py-4 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Tasks Completed</th>
                  <th className={`text-left py-4 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Efficiency</th>
                  <th className={`text-left py-4 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Success Rate</th>
                  <th className={`text-left py-4 px-4 text-sm font-medium ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Performance</th>
                </tr>
              </thead>
              <tbody>
                {filteredPerformance.map((employee, index) => {
                  const info = getEmployeeInfo(employee.employee_id);
                  const successRate = employee.tasks > 0 ? Math.round((employee.completed / employee.tasks) * 100) : 0;
                  return (
                    <motion.tr
                      key={employee.employee_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`border-b transition-colors ${isDark ? 'border-border/50 hover:bg-dark-700/50' : 'border-slate-100 hover:bg-slate-50'}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${info.gradient} flex items-center justify-center text-white text-lg`}>
                            {info.avatar}
                          </div>
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{info.name}</p>
                            <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{info.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.completed.toLocaleString()}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-24 h-2 rounded-full overflow-hidden ${isDark ? 'bg-dark-600' : 'bg-slate-200'}`}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${employee.efficiency}%` }}
                              transition={{ delay: index * 0.1, duration: 0.5 }}
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                            />
                          </div>
                          <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{employee.efficiency}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-400">★</span>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{successRate}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          employee.efficiency >= 90 ? 'bg-green-500/20 text-green-400' :
                          employee.efficiency >= 70 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {employee.efficiency >= 90 ? (
                            <><TrendingUp className="w-3 h-3" />Excellent</>
                          ) : employee.efficiency >= 70 ? (
                            <><Target className="w-3 h-3" />Good</>
                          ) : (
                            <><TrendingDown className="w-3 h-3" />Needs Work</>
                          )}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Credit Usage Tracking */}
      <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Credit Usage</h3>
            <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Track your AI credit consumption</p>
          </div>
          <Coins className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
        </div>

        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : !creditUsage ? (
          <div className={`h-48 flex items-center justify-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            <div className="text-center">
              <Coins className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No credit usage data yet</p>
            </div>
          </div>
        ) : (
          <>
            {/* Credit Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Today</span>
                </div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {creditUsage.daily.toLocaleString()}
                </p>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>credits used</p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-4 h-4 text-green-400" />
                  <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>This Week</span>
                </div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {creditUsage.weekly.toLocaleString()}
                </p>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>credits used</p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarRange className="w-4 h-4 text-purple-400" />
                  <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>This Month</span>
                </div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {creditUsage.monthly.toLocaleString()}
                </p>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>credits used</p>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-accent-purple/30">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>All Time</span>
                </div>
                <p className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {creditUsage.total.toLocaleString()}
                </p>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>total credits</p>
              </div>
            </div>

            {/* Daily Breakdown Chart */}
            {creditUsage.daily_breakdown && creditUsage.daily_breakdown.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Last 7 Days Credit Usage
                </h4>
                <div className="h-32 flex items-end gap-2">
                  {creditUsage.daily_breakdown.map((day, index) => {
                    const maxCredits = Math.max(...creditUsage.daily_breakdown.map(d => d.credits), 1);
                    const heightPercent = (day.credits / maxCredits) * 100;
                    // Parse date in UTC to avoid timezone issues
                    const [year, month, dayNum] = day.date.split('-').map(Number);
                    const date = new Date(Date.UTC(year, month - 1, dayNum));
                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()];
                    return (
                      <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full h-24 flex items-end">
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${Math.max(heightPercent, 2)}%` }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className="w-full bg-gradient-to-t from-amber-500 to-yellow-400 rounded-t-lg min-h-[4px]"
                            title={`${day.credits} credits on ${dayName}`}
                          />
                        </div>
                        <div className="text-center">
                          <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{dayName}</span>
                          <p className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{day.credits}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ROI Calculator */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ROI Summary */}
        <div className={`lg:col-span-2 p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>ROI Calculator</h3>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Track your return on investment</p>
            </div>
            <button
              onClick={() => setShowROIInfo(true)}
              className={`flex items-center gap-2 text-sm transition-colors ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <Info className="w-4 h-4" />
              How we calculate
            </button>
          </div>

          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className={`text-center p-6 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                  <DollarSign className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>${monthlyInvestment.toLocaleString()}</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Monthly Investment</p>
                </div>
                <div className={`text-center p-6 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                  <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-green-400">${(overview?.cost_savings || 0).toLocaleString()}</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Total Savings</p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-xl border border-accent-purple/30">
                  <Award className="w-8 h-8 text-accent-purple mx-auto mb-2" />
                  <p className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                    {roi}%
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Return on Investment</p>
                </div>
              </div>

              <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>Credits Used</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{(overview?.credits_used || 0).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>Projected Annual Savings</span>
                  <span className="text-green-400 font-medium">${projectedAnnualSavings.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Usage Insights */}
        <div className={`p-6 rounded-2xl border backdrop-blur-xl ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Usage Insights</h3>

          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {[
                {
                  title: 'Most Used Employee',
                  value: employeePerformance.length > 0 ? getEmployeeInfo(employeePerformance[0].employee_id).name : 'N/A',
                  subtitle: employeePerformance.length > 0 ? getEmployeeInfo(employeePerformance[0].employee_id).role : 'Start using employees',
                  icon: Users,
                  color: 'text-pink-400',
                },
                {
                  title: 'Total Tasks',
                  value: (overview?.tasks_completed || 0).toLocaleString(),
                  subtitle: `${timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : timeRange === '90d' ? 'Last 90 days' : 'Last year'}`,
                  icon: Zap,
                  color: 'text-yellow-400',
                },
                {
                  title: 'Hours Saved',
                  value: `${overview?.time_saved_hours || 0}`,
                  subtitle: 'Estimated time savings',
                  icon: Clock,
                  color: 'text-green-400',
                },
                {
                  title: 'Categories Used',
                  value: categoryUsage.categories.length.toString(),
                  subtitle: 'Different task types',
                  icon: Target,
                  color: 'text-purple-400',
                },
              ].map((insight) => (
                <div key={insight.title} className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <insight.icon className={`w-5 h-5 ${insight.color}`} />
                    <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{insight.title}</span>
                  </div>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{insight.value}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{insight.subtitle}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ROI Info Modal */}
      {showROIInfo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`w-full max-w-lg rounded-2xl p-6 ${isDark ? 'bg-dark-800 border border-white/10' : 'bg-white shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>How We Calculate ROI</h3>
              <button
                onClick={() => setShowROIInfo(false)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-text-muted' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Time Saved</h4>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  We estimate 5 minutes saved per task completed by AI employees. This is based on the average time it would take a human to perform similar tasks.
                </p>
              </div>

              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Cost Savings</h4>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  We calculate cost savings by multiplying the time saved (in hours) by an estimated labor cost of $25/hour. This represents the value of work done by AI employees.
                </p>
              </div>

              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Return on Investment (ROI)</h4>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  ROI is calculated as: (Cost Savings / Monthly Subscription Cost) × 100. For example, if you save $1,000 with a $299 subscription, your ROI is approximately 334%.
                </p>
              </div>

              <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Projected Annual Savings</h4>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  This is your current period savings multiplied by 12 to estimate yearly savings if you maintain the same usage level.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowROIInfo(false)}
              className="w-full mt-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
            >
              Got it
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
